import type { ReconcileBLResult } from "@/lib/api/extract";

export type DetailTab =
  | "missing"
  | "extra"
  | "duplicates"
  | "documents"
  | "mismatches"
  | "found";

export type ReconcileIssueCounts = {
  missing: number;
  extra: number;
  duplicates: number;
  found: number;
  ddtMismatch: number;
  fatturaMismatch: number;
};

export function statusClass(status: ReconcileBLResult["status"]): string {
  if (status === "aligned") return "verify-status-badge--aligned";
  if (status === "under") return "verify-status-badge--under";
  return "verify-status-badge--risk";
}

export function statusLabel(status: ReconcileBLResult["status"]): string {
  if (status === "aligned") return "Allineato";
  if (status === "under") return "Mancante";
  if (status === "over") return "Extra";
  return "Da verificare";
}

function statusScore(status: ReconcileBLResult["status"]): number {
  if (status === "mixed" || status === "over") return 3;
  if (status === "under") return 2;
  return 1;
}

export function sortReconcileItems(
  items: ReconcileBLResult[],
): ReconcileBLResult[] {
  return [...items].sort((a, b) => {
    const duplicateDiff =
      (b.duplicate_pairs_count ?? 0) - (a.duplicate_pairs_count ?? 0);
    if (duplicateDiff !== 0) return duplicateDiff;

    const statusDiff = statusScore(b.status) - statusScore(a.status);
    if (statusDiff !== 0) return statusDiff;

    return a.bl.localeCompare(b.bl);
  });
}

export function getIssueCounts(item: ReconcileBLResult): ReconcileIssueCounts {
  return {
    missing: item.missing_containers?.length ?? 0,
    extra: item.extra_containers?.length ?? 0,
    duplicates: item.duplicate_pairs_count ?? 0,
    found: item.found_containers?.length ?? 0,
    ddtMismatch: item.ddt_mismatch_count ?? 0,
    fatturaMismatch: item.fattura_mismatch_count ?? 0,
  };
}

export function isMonitoredReconcileItem(item: ReconcileBLResult): boolean {
  return item.status !== "aligned";
}

export function getDefaultDetailTab(item: ReconcileBLResult): DetailTab {
  const counts = getIssueCounts(item);

  if (counts.duplicates > 0) return "duplicates";
  if (counts.extra > 0) return "extra";
  if (counts.missing > 0) return "missing";
  if (counts.ddtMismatch > 0 || counts.fatturaMismatch > 0) return "mismatches";
  if (counts.found > 0) return "found";
  return "documents";
}

export function getWorklistSummary(item: ReconcileBLResult): string {
  const counts = getIssueCounts(item);
  const issueParts: string[] = [];

  const expectedCount =
    item.expected_count ?? item.expected_containers?.length ?? 0;
  const expectedText = `${expectedCount} container ${
    expectedCount === 1 ? "atteso" : "attesi"
  }`;

  if (counts.duplicates > 0) {
    issueParts.push(
      `${counts.duplicates} ${counts.duplicates === 1 ? "doppio" : "doppi"}`,
    );
  }

  if (counts.extra > 0) {
    issueParts.push(`${counts.extra} extra`);
  }

  if (counts.ddtMismatch > 0) {
    issueParts.push(
      `${counts.ddtMismatch} mismatch ${counts.ddtMismatch === 1 ? "DDT" : "DDT"}`,
    );
  }

  if (counts.fatturaMismatch > 0) {
    issueParts.push(
      `${counts.fatturaMismatch} mismatch ${counts.fatturaMismatch === 1 ? "fattura" : "fattura"}`,
    );
  }

  const pendingSummary =
    counts.missing > 0 ? `${counts.missing} container mancanti` : null;

  let mainSummary = "";

  if (issueParts.length === 0 && pendingSummary) {
    mainSummary = pendingSummary;
  } else if (issueParts.length === 0) {
    mainSummary = "Nessuna anomalia riscontrata";
  } else {
    const totalIssues =
      counts.extra +
      counts.duplicates +
      counts.ddtMismatch +
      counts.fatturaMismatch;
    const issueLabel = `${totalIssues} criticita`;
    const issueSummary = `${issueLabel}: ${issueParts.join(" · ")}`;

    mainSummary = !pendingSummary
      ? issueSummary
      : `${issueSummary} · ${pendingSummary}`;
  }

  return `${expectedText} · ${mainSummary}`;
}

export function getActiveDetail(
  selectedItem: ReconcileBLResult | null,
  detailTab: DetailTab,
): string[] {
  if (!selectedItem) return [];
  if (detailTab === "missing") return selectedItem.missing_containers ?? [];
  if (detailTab === "extra") return selectedItem.extra_containers ?? [];
  if (detailTab === "found") return selectedItem.found_containers ?? [];
  return [];
}
